// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription, Observable } from 'rxjs';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';

import { Store, select } from '@ngrx/store';
import { BackAction, QueueAlertAction, AlertType, readParameterFromRouterState } from '@app/core';
import { GroupsService, CreateGroupRequest } from '../../core/services/group.service';
import { State } from '../../state/index';
import { LoadingGroupsAction } from '../../state/groups/groups.actions';
import { Group, GroupDetail } from '@app/access-management/core/models/groups.model';
import { ActivatedRoute } from '@angular/router';
import { map, filter, tap, take } from 'rxjs/operators';
import { selectGroupDetail } from '@app/access-management/state/group-details/group-details.selectors';
import { INPUT_TEXT_REQUIRED_ERROR, NAME_PATTERN_ERROR, DESCRIPTION_PATTERN_ERROR, NAME_PATTERN, DESCRIPTION_PATTERN
} from '@app/core/services/input-text-validator.service';
import { LoggedUserService } from '@app/core/services/logged-user.service';

@Component({
  selector: 'app-group-edit',
  templateUrl: './group-edit.component.html',
  styleUrls: ['./group-edit.component.scss']
})
export class GroupEditComponent implements OnInit, OnDestroy {
  readonly group$: Observable<Group | undefined>;
  groupForm: FormGroup;
  submitSubscription: Subscription | undefined = undefined;
  groupNameControl: FormControl;
  groupDescriptionControl: FormControl;
  accountControl: FormControl;
  _originalGroup: Group | undefined;

  nameControlErrors = [
    INPUT_TEXT_REQUIRED_ERROR,
    NAME_PATTERN_ERROR
  ];

  descriptionControlErrors = [
    INPUT_TEXT_REQUIRED_ERROR,
    DESCRIPTION_PATTERN_ERROR,
  ];

  _isUserAllowed = this.loggedUserService.areRolesAllowed(['lf-admin', 'super-admin']);

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<State>,
    private formBuilder: FormBuilder,
    private groupsService: GroupsService,
    private loggedUserService: LoggedUserService

  ) {
    const groupId: any = readParameterFromRouterState(this.activatedRoute, 'id');
    const groupDetail$: Observable<GroupDetail> = this.store.pipe(
      select(selectGroupDetail(groupId)),
      filter<GroupDetail>(groupDetail => !!groupDetail)
    );

    this.group$ = groupDetail$.pipe(map(groupDetail => groupDetail.group));

    this.groupForm = this.buildGroupForm();
    this.accountControl = this.groupForm.get('account') as FormControl;
    this.groupNameControl = this.groupForm.get('groupName') as FormControl;
    this.groupDescriptionControl = this.groupForm.get('groupDescription') as FormControl;
  }

  ngOnInit() {
    this.group$.subscribe((group: any) => {
      this._originalGroup = group;
      this.groupNameControl.setValue(group.group_name);
      this.groupDescriptionControl.setValue(group.description);
      this.setRolePermissions();
    });
  }

  setRolePermissions () {
    if (!this._isUserAllowed) {
      this.setSimpleAccountControl();
    } else {
      this.setAccountListValueFromGroup();
    }
  }

  setAccountListValueFromGroup () {
    if (!this._originalGroup) {
      return;
    }

    const accountControlValue = {
      id: '',
      sfid: this._originalGroup.account_id,
      name: this._originalGroup.account_name
    };
    this.accountControl.setValue(accountControlValue);
  }

  setSimpleAccountControl () {
    if (!this._originalGroup) {
      return;
    }

    this.accountControl.setValue(this._originalGroup.account_name);
    this.accountControl.disable();
  }

  getAccountControlValue () {
    if (!this._originalGroup) {
      return;
    }

    return this._isUserAllowed ?
      this.groupForm.value['account'].sfid : this._originalGroup.account_id;
  }

  ngOnDestroy() {
    if (this.submitSubscription) {
      this.submitSubscription.unsubscribe();
    }
  }

  cancel() {
    this.goBackToListPage();
  }

  isFormValid () {
    return this.groupForm.dirty && this.groupForm.valid;
  }


  submit() {
    if (!this._originalGroup) {
      return;
    }

    if (!this.isFormValid()) {
      return;
    }

    const request: CreateGroupRequest = {
      group_name: this.groupForm.value['groupName'],
      description: this.groupForm.value['groupDescription'],
      account_id: this.getAccountControlValue()
    };
    this.submitSubscription = this.groupsService
      .updateGroup(this._originalGroup.group_id, request).subscribe(
      () => {
        this.goBackToListPage();
        this.store.dispatch(new LoadingGroupsAction());
        this.store.dispatch(
          new QueueAlertAction({
            alertText: `Group updated succeed`,
            alertType: AlertType.SUCCESS
          })
        );
      },
      () => {
        this.goBackToListPage();
        this.store.dispatch(
          new QueueAlertAction({
            alertText: `Failed to update a group.`,
            alertType: AlertType.ERROR
          })
        );
      }
    );
  }

  private goBackToListPage() {
    this.store.dispatch(new BackAction());
  }

  private buildGroupForm() {
    return this.formBuilder.group({
      groupName: ['', [
          Validators.required,
          Validators.pattern(NAME_PATTERN)
        ]],
      groupDescription: ['', [
          Validators.required,
          Validators.pattern(DESCRIPTION_PATTERN)
        ]],
      account: [null, [Validators.required]]
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/groups/group-edit/group-edit.component.ts